/** Tiles the docs home shows under Get started. */
export const GET_STARTED_LIMIT = 3

/** Tiles the docs home shows under Popular. */
export const POPULAR_LIMIT = 6

export type HelpCenterHomeSection = "GET_STARTED" | "POPULAR"

export function homeSectionLimit(section: HelpCenterHomeSection): number {
	return section === "GET_STARTED" ? GET_STARTED_LIMIT : POPULAR_LIMIT
}

/** Custom sections a docs home holds after Popular. */
export const MAX_CUSTOM_HOME_SECTIONS = 6

/** Articles someone can hand-pick into one custom section. */
export const PICKED_SECTION_ARTICLE_LIMIT = 6

/** Tiles a custom section shows, two full rows of three. */
export const CUSTOM_SECTION_TILES = 6

export interface HomeSectionGroup<T> {
	name: string
	articles: readonly T[]
}

export interface FallbackHomeSections<T> {
	/** The first group carries the section, so its own name is the honest label. */
	getStarted: { label: string; articles: T[] }
	popular: { articles: T[] }
}

/**
 * The home a workspace gets before anyone curates it: the first group opens the
 * page, then every other group lends its first article until Popular is full.
 */
export function splitFallbackHomeSections<T>(
	groups: readonly HomeSectionGroup<T>[],
): FallbackHomeSections<T> {
	const filled = groups.filter((group) => group.articles.length > 0)
	const first = filled[0]
	if (!first)
		return {
			getStarted: { label: "", articles: [] },
			popular: { articles: [] },
		}

	const getStarted = first.articles.slice(0, GET_STARTED_LIMIT)
	const taken = new Set<T>(getStarted)
	const popular: T[] = []

	for (const group of filled.slice(1)) {
		const article = group.articles[0]
		if (!article || taken.has(article)) continue
		taken.add(article)
		popular.push(article)
		if (popular.length === POPULAR_LIMIT) break
	}

	// Too few groups to fill the row, so the groups themselves lend their rest.
	for (const group of filled) {
		if (popular.length === POPULAR_LIMIT) break
		for (const article of group.articles) {
			if (taken.has(article)) continue
			taken.add(article)
			popular.push(article)
			if (popular.length === POPULAR_LIMIT) break
		}
	}

	return {
		getStarted: { label: first.name, articles: getStarted },
		popular: { articles: popular },
	}
}

export interface CustomHomeSectionRows<T> {
	id: string
	title: string
	articles: readonly T[]
}

/**
 * An article shows once on the docs home: Get started and Popular keep theirs,
 * then each custom section keeps up to CUSTOM_SECTION_TILES an earlier one did
 * not take. A section left with nothing drops out, so readers never see an
 * empty heading.
 */
export function dedupeCustomHomeSections<T, S extends CustomHomeSectionRows<T>>(
	sections: readonly S[],
	keyOf: (article: T) => string,
	shownAbove: Iterable<string>,
): S[] {
	const taken = new Set(shownAbove)
	const result: S[] = []
	for (const section of sections) {
		const articles: T[] = []
		for (const article of section.articles) {
			if (articles.length === CUSTOM_SECTION_TILES) break
			const key = keyOf(article)
			if (taken.has(key)) continue
			taken.add(key)
			articles.push(article)
		}
		if (articles.length === 0) continue
		result.push({ ...section, articles })
	}
	return result
}
